import { useState, useRef, useEffect } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform, useInView } from "framer-motion";
import { HeroSmoke } from "../components/HeroSmoke";
import { Reveal, MaskLines, MaskLinesInView } from "../components/Reveal";
import { ProductCard } from "../components/ProductCard";
import { EnquiryModal } from "../components/EnquiryModal";
import { PRODUCTS, CATEGORIES } from "../data/products";
import { IMAGES, BRAND } from "../data/brand";
import { useSEO } from "../lib/useSEO";

const ease = [0.16, 1, 0.3, 1];

const Counter = ({ target, suffix = "", duration = 1600 }) => {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: "-40px" });
  const [val, setVal] = useState(0);
  useEffect(() => {
    if (!inView) return;
    let start;
    let raf;
    const step = (t) => {
      if (!start) start = t;
      const p = Math.min((t - start) / duration, 1);
      setVal(Math.floor((1 - Math.pow(1 - p, 3)) * target));
      if (p < 1) raf = requestAnimationFrame(step);
    };
    raf = requestAnimationFrame(step);
    return () => cancelAnimationFrame(raf);
  }, [inView, target, duration]);
  return <span ref={ref}>{val}{suffix}</span>;
};

const CRAFT = [
  { n: "01", t: "Select", d: "Carefully selected fragrance ingredients, chosen for character and consistency." },
  { n: "02", t: "Blend", d: "Balanced fragrance profiles developed with over four decades of experience." },
  { n: "03", t: "Craft", d: "Traditional incense-making techniques passed through generations." },
  { n: "04", t: "Pack", d: "Carefully prepared and packed for everyday use and elevated spaces." },
  { n: "05", t: "Experience", d: "A fragrance that transforms the space it fills — calm, warmth, character." },
];

const WHY = [
  { t: "40+ Years of Experience", d: "Four decades of dedicated incense craftsmanship." },
  { t: "Authentic Fragrance", d: "Carefully developed, honest aromatic profiles." },
  { t: "Made in Bangalore", d: "A proudly homegrown Indian fragrance brand." },
  { t: "Wide Selection", d: "From everyday economy to refined premium collections." },
  { t: "Trusted Craftsmanship", d: "Consistency built through years of quiet mastery." },
];

export default function Home() {
  const [enquire, setEnquire] = useState(false);
  useSEO(
    "SK Fragrances | Incense Stick Manufacturer in Bangalore",
    "SK Fragrances is a Bangalore-based incense stick manufacturer established in 1980, offering economy, premium and assorted incense fragrances crafted with decades of experience."
  );

  const featured = PRODUCTS.slice(0, 4);
  const storyRef = useRef(null);
  const { scrollYProgress: storyP } = useScroll({ target: storyRef, offset: ["start end", "end start"] });
  const storyImgY = useTransform(storyP, [0, 1], [-40, 40]);

  const bsRef = useRef(null);
  const { scrollYProgress: bsP } = useScroll({ target: bsRef, offset: ["start end", "end start"] });
  const bsY = useTransform(bsP, [0, 1], ["-12%", "12%"]);

  return (
    <div data-testid="home-page">
      {/* HERO */}
      <section className="relative overflow-hidden pt-32 pb-20 md:pt-28 md:pb-0 md:min-h-screen md:flex md:items-center">
        <div className="max-w-[1400px] mx-auto px-5 md:px-10 grid md:grid-cols-2 gap-10 md:gap-10 items-center w-full">
          <div className="order-1">
            <motion.p
              initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.15, duration: 1 }}
              className="text-sm tracking-[0.35em] text-gold mb-6"
            >
              SINCE 1980 · CRAFTED IN {BRAND.city.toUpperCase()}
            </motion.p>
            <MaskLines
              lines={["A Fragrance", "That Feels", "Like Home."]}
              className="font-serif text-6xl sm:text-7xl lg:text-8xl leading-[0.95] text-charcoal"
              delay={0.3}
            />
            <motion.p
              initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.6, duration: 0.9, ease }}
              className="mt-8 max-w-md text-charcoal/70 leading-relaxed"
            >
              Crafted with care in Bangalore since 1980, SK Fragrances brings together traditional
              incense craftsmanship and carefully developed fragrance profiles for everyday rituals
              and moments of calm.
            </motion.p>
            <motion.div
              initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.8, duration: 0.9, ease }}
              className="mt-10 flex flex-wrap items-center gap-5"
            >
              <Link to="/products" data-testid="hero-explore-btn" className="btn-line bg-charcoal text-ivory px-8 py-4 text-sm tracking-[0.15em] hover:text-charcoal">
                EXPLORE FRAGRANCES →
              </Link>
              <button onClick={() => setEnquire(true)} data-testid="hero-enquire-btn" className="nav-link text-sm tracking-[0.15em] text-charcoal py-4">
                Enquire Now
              </button>
            </motion.div>
          </div>
          <div className="order-2 h-[42vh] sm:h-[50vh] md:h-[80vh]">
            <HeroSmoke />
          </div>
        </div>
        <div className="absolute left-5 md:left-10 bottom-8 hidden md:flex flex-col items-center gap-3">
          <span className="text-[10px] tracking-[0.3em] text-charcoal/50 [writing-mode:vertical-rl] rotate-180">SCROLL TO DISCOVER</span>
          <span className="w-px h-16 bg-gradient-to-b from-gold to-transparent" />
        </div>
      </section>

      {/* HERITAGE STRIP */}
      <section className="bg-burgundy text-ivory py-20 md:py-28" data-testid="heritage-strip">
        <div className="max-w-[1400px] mx-auto px-5 md:px-10">
          <Reveal>
            <p className="text-sm tracking-[0.3em] text-gold mb-12">OVER FOUR DECADES OF FRAGRANCE CRAFTSMANSHIP</p>
          </Reveal>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-12 md:gap-8">
            {[
              { num: <><Counter target={40} />+</>, label: "Years of Craftsmanship" },
              { num: <Counter target={41} />, label: "Fragrance Expressions" },
              { num: "1980", label: `Established in ${BRAND.city}` },
            ].map((s, i) => (
              <Reveal key={i} delay={i * 0.12}>
                <div className="border-t border-ivory/25 pt-6">
                  <div className="font-serif text-7xl md:text-8xl leading-none text-ivory">{s.num}</div>
                  <div className="mt-3 text-sm tracking-[0.2em] text-ivory/70">{s.label.toUpperCase()}</div>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* OUR STORY */}
      <section ref={storyRef} className="relative py-24 md:py-36 bg-ivory" data-testid="story-section">
        <div className="max-w-[1400px] mx-auto px-5 md:px-10 grid md:grid-cols-2 gap-14 items-center">
          <div className="relative overflow-hidden aspect-[4/5] order-2 md:order-1">
            <motion.img style={{ y: storyImgY }} src={IMAGES.story} alt="Crafting incense" className="w-full h-[115%] object-cover" />
            <div className="absolute bottom-5 left-5 bg-ivory/90 backdrop-blur px-5 py-3">
              <span className="font-serif text-3xl">1980</span>
              <span className="block text-[10px] tracking-[0.25em] text-charcoal/60">THE BEGINNING</span>
            </div>
          </div>
          <div className="order-1 md:order-2">
            <p className="text-sm tracking-[0.3em] text-gold mb-6">OUR STORY</p>
            <MaskLinesInView
              lines={["Born in Bangalore.", "Crafted Through", "Generations."]}
              className="font-serif text-5xl md:text-6xl leading-[1] text-charcoal"
            />
            <div className="mt-8 space-y-5 text-charcoal/70 leading-relaxed max-w-lg">
              <p>Established in 1980 by {BRAND.founder} in Bangalore, SK Fragrances began as a humble homegrown venture with a simple belief — that every incense stick should create a fragrance worth remembering.</p>
              <p>Over four decades, that belief has evolved into a trusted incense manufacturing brand, combining traditional craftsmanship with carefully developed modern fragrance profiles.</p>
            </div>
            <Link to="/about" data-testid="story-cta" className="mt-10 inline-block nav-link text-sm tracking-[0.15em] text-charcoal">
              Discover Our Story →
            </Link>
          </div>
        </div>
      </section>

      {/* PRODUCT CATEGORIES */}
      <section className="py-24 md:py-32 bg-cream" data-testid="categories-section">
        <div className="max-w-[1400px] mx-auto px-5 md:px-10">
          <div className="flex flex-col md:flex-row md:items-end justify-between gap-6 mb-14">
            <div>
              <p className="text-sm tracking-[0.3em] text-gold mb-4">THE COLLECTIONS</p>
              <MaskLinesInView lines={["Find Your Fragrance"]} className="font-serif text-5xl md:text-7xl text-charcoal" />
            </div>
            <p className="text-charcoal/60 max-w-xs text-sm">Three collections, one philosophy — fragrance worth remembering, for every space and every moment.</p>
          </div>
          <div className="grid md:grid-cols-3 gap-6">
            {CATEGORIES.map((c, i) => (
              <Reveal key={c.key} delay={i * 0.1}>
                <Link to={`/products?category=${c.key}`} data-testid={`category-card-${c.key}`} className="group block relative overflow-hidden bg-charcoal">
                  <div className="relative aspect-[3/4] overflow-hidden">
                    <img src={c.image} alt={c.title} className="w-full h-full object-cover opacity-90 transition-transform duration-[900ms] ease-[cubic-bezier(0.16,1,0.3,1)] group-hover:scale-[1.05]" />
                    <div className="absolute inset-0 bg-gradient-to-t from-charcoal/85 via-charcoal/20 to-transparent" />
                  </div>
                  <div className="absolute inset-0 p-7 flex flex-col justify-between text-ivory">
                    <div className="flex items-center justify-between">
                      <span className="font-serif text-2xl text-gold">{c.number}</span>
                      <span className="text-[10px] tracking-[0.25em] text-ivory/70">{c.count} PRODUCTS</span>
                    </div>
                    <div>
                      <span className="block h-px w-10 bg-gold mb-4 transition-all duration-500 group-hover:w-20" />
                      <h3 className="font-serif text-4xl leading-none">{c.title}</h3>
                      <p className="mt-2 text-sm text-ivory/75 max-w-[16rem]">{c.tagline}</p>
                      <span className="mt-4 inline-block text-xs tracking-[0.15em] text-gold">Explore {c.title.split(" ")[0]} →</span>
                    </div>
                  </div>
                </Link>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* MARQUEE */}
      <section className="py-8 bg-charcoal text-ivory overflow-hidden border-y border-gold/20" data-testid="marquee">
        <div className="marquee-track">
          {[...Array(2)].map((_, k) => (
            <span key={k} className="flex items-center">
              {["Sandalwood", "Rose", "Jasmine", "Oudh", "Saffron", "Lavender", "Mogra", "Amber", "Vanilla"].map((w) => (
                <span key={w} className="font-serif text-4xl md:text-5xl mx-8 text-ivory/90 flex items-center gap-8">
                  {w} <span className="text-gold text-2xl">✦</span>
                </span>
              ))}
            </span>
          ))}
        </div>
      </section>

      {/* FEATURED */}
      <section className="py-24 md:py-32 bg-ivory" data-testid="featured-section">
        <div className="max-w-[1400px] mx-auto px-5 md:px-10">
          <div className="mb-14">
            <p className="text-sm tracking-[0.3em] text-gold mb-4">SELECTED FRAGRANCES</p>
            <MaskLinesInView lines={["Our Fragrance Collection"]} className="font-serif text-5xl md:text-7xl text-charcoal" />
            <p className="mt-4 text-charcoal/60 max-w-md text-sm">Discover some of the fragrances from our collection, each crafted to leave an impression.</p>
          </div>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-5 md:gap-6">
            {featured.map((p, i) => <ProductCard key={p.id} product={p} index={i} />)}
          </div>
          <Reveal delay={0.1}>
            <div className="mt-14 text-center">
              <Link to="/products" data-testid="featured-viewall" className="btn-line inline-block border border-charcoal text-charcoal px-10 py-4 text-sm tracking-[0.15em] hover:text-charcoal">
                VIEW ALL FRAGRANCES →
              </Link>
            </div>
          </Reveal>
        </div>
      </section>

      {/* FROM ESSENCE TO AROMA */}
      <section className="py-24 md:py-36 bg-cream" data-testid="craft-section">
        <div className="max-w-[1400px] mx-auto px-5 md:px-10">
          <div className="mb-16 max-w-2xl">
            <p className="text-sm tracking-[0.3em] text-gold mb-4">THE CRAFT</p>
            <MaskLinesInView lines={["From Essence", "to Aroma"]} className="font-serif text-5xl md:text-7xl text-charcoal leading-[0.95]" />
          </div>
          <div className="grid md:grid-cols-2 gap-14 items-start">
            <div className="relative overflow-hidden aspect-[4/5]">
              <img src={IMAGES.craft} alt="Incense craftsmanship" className="w-full h-full object-cover" />
            </div>
            <div>
              {CRAFT.map((c, i) => (
                <Reveal key={c.n} delay={i * 0.08}>
                  <div className="group flex gap-6 py-6 border-b border-charcoal/12 hover:border-gold transition-colors duration-500">
                    <span className="font-serif text-3xl text-gold shrink-0 w-12">{c.n}</span>
                    <div>
                      <h4 className="font-serif text-3xl text-charcoal group-hover:text-burgundy transition-colors duration-300">{c.t}</h4>
                      <p className="mt-1 text-charcoal/65 text-sm max-w-md">{c.d}</p>
                    </div>
                  </div>
                </Reveal>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* WHY SK */}
      <section className="py-24 md:py-32 bg-ivory" data-testid="why-section">
        <div className="max-w-[1400px] mx-auto px-5 md:px-10">
          <div className="mb-14 max-w-2xl">
            <p className="text-sm tracking-[0.3em] text-gold mb-4">WHY SK FRAGRANCES</p>
            <MaskLinesInView lines={["Craftsmanship", "You Can Trust."]} className="font-serif text-5xl md:text-6xl text-charcoal leading-[0.95]" />
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-px bg-charcoal/10">
            {WHY.map((w, i) => (
              <Reveal key={w.t} delay={(i % 3) * 0.08}>
                <div className="bg-ivory p-8 h-full min-h-[180px] flex flex-col justify-between group hover:bg-white transition-colors duration-500">
                  <span className="font-serif text-2xl text-gold">0{i + 1}</span>
                  <div className="mt-6">
                    <h4 className="font-serif text-2xl text-charcoal">{w.t}</h4>
                    <p className="mt-2 text-sm text-charcoal/60">{w.d}</p>
                  </div>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* BRAND STATEMENT */}
      <section ref={bsRef} className="relative h-[80vh] overflow-hidden flex items-center" data-testid="brand-statement">
        <motion.div style={{ y: bsY }} className="absolute inset-0 -z-0">
          <img src={IMAGES.brandStatement} alt="Fragrance" className="w-full h-[124%] object-cover" />
          <div className="absolute inset-0 bg-burgundy/70 mix-blend-multiply" />
          <div className="absolute inset-0 bg-charcoal/40" />
        </motion.div>
        <div className="relative max-w-[1400px] mx-auto px-5 md:px-10 text-ivory">
          <MaskLinesInView lines={["Every Fragrance", "Tells a Story."]} className="font-serif text-5xl md:text-8xl leading-[0.95]" />
          <Reveal delay={0.2}>
            <p className="mt-6 max-w-lg text-ivory/85 leading-relaxed">
              From the first spark to the final note, every SK fragrance is crafted to leave an impression.
            </p>
            <Link to="/products" data-testid="brand-statement-cta" className="btn-line mt-8 inline-block bg-ivory text-charcoal px-9 py-4 text-sm tracking-[0.15em]">
              EXPLORE THE COLLECTION →
            </Link>
          </Reveal>
        </div>
      </section>

      {/* ENQUIRY CTA */}
      <section className="py-24 md:py-32 bg-charcoal text-ivory text-center" data-testid="enquiry-cta">
        <div className="max-w-3xl mx-auto px-5">
          <p className="text-sm tracking-[0.3em] text-gold mb-6">LOOKING FOR SK FRAGRANCES?</p>
          <MaskLinesInView lines={["Let's Talk Fragrance."]} className="font-serif text-5xl md:text-7xl" />
          <Reveal delay={0.15}>
            <p className="mt-6 text-ivory/70 max-w-xl mx-auto">
              Retail enquiries, bulk requirements or distribution opportunities — we would love to hear from you.
            </p>
            <div className="mt-10 flex flex-wrap justify-center gap-5">
              <button onClick={() => setEnquire(true)} data-testid="cta-enquire-btn" className="btn-line bg-gold text-charcoal px-9 py-4 text-sm tracking-[0.15em]">
                ENQUIRE NOW →
              </button>
              <Link to="/contact" className="btn-line border border-ivory/40 text-ivory px-9 py-4 text-sm tracking-[0.15em] hover:text-charcoal">
                Contact Us
              </Link>
            </div>
          </Reveal>
        </div>
      </section>

      <EnquiryModal open={enquire} onClose={() => setEnquire(false)} product={null} />
    </div>
  );
}
